'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth, type ImpersonationSession } from '@/lib/auth-context';

/**
 * Tranche G — the "you are acting as someone else" strip.
 *
 * Mounted ONCE, in `providers.tsx` beside `LocaleRestorer`. That is the only
 * place that covers `/dashboard/*` as well as `/admin/*`: the dashboard is a
 * different layout tree, and impersonation is precisely the case where the
 * admin is looking at the DASHBOARD rather than the console.
 *
 * **Hebrew and RTL**, with the strings hardcoded here rather than routed
 * through `src/locales/` — which is what the rest of the admin console does
 * (CONVENTIONS.md's web rules; decisions `0032` and `0036`). `G-impersonation.md`
 * says English/LTR and is stale on this: the console was translated in
 * 2026-08-07 precisely because a colleague who does not read English fluently
 * got access, and that colleague is exactly who uses impersonation. A warning
 * nobody can read is not a warning.
 *
 * Renders nothing at all when no impersonation is or was active, so every
 * ordinary page pays one boolean.
 */

/** Start renewing this long before the token dies. */
const RENEW_AT_MS = 2 * 60 * 1000;

/**
 * Renewal requires a real pointer/key event inside this window.
 *
 * Without it a plain timer makes the concierge session IMMORTAL — a tab left
 * open overnight would still hold a live owner-scoped token in the morning —
 * and that throws away the whole reason the token is short-lived. No activity
 * means the session ends, and the strip says so out loud.
 */
const ACTIVITY_WINDOW_MS = 5 * 60 * 1000;

/**
 * Height the rest of the app reserves for the strip, published as a custom
 * property on `<html>`.
 *
 * `AdminNav`'s rail and `AdminShell`'s topbar are both `position: sticky;
 * top: 0`, so a fixed strip at `top: 0` sits on top of them. They read
 * `top: var(--impersonation-h, 0px)` instead — the `0px` default is the
 * load-bearing half: with no impersonation the property does not exist and
 * every one of those rules is inert, so an ordinary page load has exactly
 * the layout it had before this feature.
 */
const OFFSET_VAR = '--impersonation-h';
const BAR_HEIGHT = 40;

const frame: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 100,
  pointerEvents: 'none',
  borderWidth: 3,
  borderStyle: 'solid',
  // This repo has NO global `* { box-sizing: border-box }` reset (see
  // AuroraForm.module.css's note), so say it here: the border must live
  // INSIDE the viewport box, never add 6px of scrollable overflow to it.
  boxSizing: 'border-box',
};

const bar: CSSProperties = {
  position: 'fixed',
  insetInlineStart: 0,
  insetInlineEnd: 0,
  top: 0,
  zIndex: 100,
  minHeight: BAR_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-4)',
  flexWrap: 'wrap',
  padding: '6px 14px',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  lineHeight: 1.3,
  // Explicit, never left to `auto`: this repo has been bitten repeatedly by
  // alignment that reads correctly in one direction only.
  textAlign: 'start',
  color: 'var(--text-primary)',
};

const trailingControl: CSSProperties = {
  marginInlineStart: 'auto',
  border: '1px solid currentColor',
  borderRadius: 'var(--r-full)',
  background: 'transparent',
  color: 'inherit',
  font: 'inherit',
  fontWeight: 600,
  padding: '4px 14px',
  cursor: 'pointer',
};

/** m:ss, never negative. Rendered inside a `<bdi>` — see its call site. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${ss < 10 ? '0' : ''}${ss}`;
}

/**
 * Publishes the strip's height on `<html>` while it is on screen, and REMOVES
 * the property when it goes. Measured rather than hardcoded because the strip
 * wraps to two or three lines on a phone, where a fixed 40 would leave the
 * console's topbar half-covered. jsdom reports 0 for every box, so the
 * constant is also the fallback.
 */
function useViewportOffset(ref: RefObject<HTMLDivElement | null>): void {
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const apply = () => {
      const measured = ref.current?.getBoundingClientRect().height ?? 0;
      root.style.setProperty(OFFSET_VAR, `${Math.round(measured) || BAR_HEIGHT}px`);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      root.style.removeProperty(OFFSET_VAR);
    };
  }, [ref]);
}

export function ImpersonationBanner() {
  // `useAuth`, not `useAuthInternal`: this is an ordinary consumer of the
  // public surface, and the internal hook exists only for the tRPC fetch
  // wiring. Everything the strip needs — the session, the expired flag, and
  // both exits — is on the public value.
  const auth = useAuth();

  // The active strip is a SEPARATE component on purpose. It owns the
  // `trpc.admin.actAs` mutation hook, which therefore only exists while an
  // impersonation is live — so mounting this provider-level component never
  // reaches into `trpc.admin` on an ordinary page (or in a suite whose trpc
  // mock has no `admin` branch).
  if (auth.impersonation) {
    return <ActiveBanner session={auth.impersonation} />;
  }
  if (auth.impersonationExpired) {
    return <ExpiredNotice onDismiss={auth.stopImpersonation} />;
  }
  return null;
}

function ActiveBanner({ session }: { session: ImpersonationSession }) {
  const auth = useAuth();
  const [now, setNow] = useState<number>(() => Date.now());
  const barRef = useRef<HTMLDivElement | null>(null);

  // Last real human interaction. A ref, not state: it must not re-render the
  // page on every mouse click.
  const lastActivityRef = useRef<number>(Date.now());
  // Belt and braces against a slow `actAs`: `isPending` only becomes true on
  // the next render, and this effect can run again before that lands.
  const renewingRef = useRef(false);

  useViewportOffset(barRef);

  const renewMutation = trpc.admin.actAs.useMutation({
    onSuccess: (res: { accessToken: string }) => {
      renewingRef.current = false;
      auth.renewImpersonation(res.accessToken);
    },
    onError: () => {
      // Leave it to expire. A failed renewal is not a reason to keep a token
      // that is about to stop working anyway.
      renewingRef.current = false;
    },
  });

  useEffect(() => {
    const mark = () => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener('pointerdown', mark);
    window.addEventListener('keydown', mark);
    return () => {
      window.removeEventListener('pointerdown', mark);
      window.removeEventListener('keydown', mark);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remainingMs = session.expiresAt - now;
  const { expireImpersonation } = auth;
  const mutate = renewMutation.mutate;

  useEffect(() => {
    if (remainingMs <= 0) {
      expireImpersonation();
      return;
    }
    if (remainingMs > RENEW_AT_MS) return;
    if (renewingRef.current) return;
    if (Date.now() - lastActivityRef.current > ACTIVITY_WINDOW_MS) return;
    renewingRef.current = true;
    mutate({ businessId: session.businessId });
  }, [remainingMs, session.businessId, expireImpersonation, mutate]);

  // Catches the admin who tabbed away: the tab title is the only part of this
  // warning that is visible from another tab. Hebrew for the same reason the
  // strip is — it is read by a person, in a tab strip.
  useEffect(() => {
    const previous = document.title;
    document.title = `[ניהול כבעלים: ${session.businessName}] ${previous}`;
    return () => {
      document.title = previous;
    };
  }, [session.businessName]);

  const handleExit = useCallback(() => {
    auth.stopImpersonation();
    if (typeof window === 'undefined') return;
    // A FULL navigation, deliberately. `router.push` would keep the React
    // Query cache, so the console would re-render this business's admin rows
    // from data fetched under the impersonation token — or, on the next
    // impersonation, serve the previous one's.
    window.location.assign(`/admin/businesses/${session.businessId}`);
  }, [auth, session.businessId]);

  return (
    <>
      <div aria-hidden="true" style={{ ...frame, borderColor: 'var(--warn)' }} />
      <div
        ref={barRef}
        dir="rtl"
        lang="he"
        role="status"
        aria-live="polite"
        data-testid="impersonation-banner"
        style={{ ...bar, background: 'var(--warn-wash)', borderBottom: '1px solid var(--warn)' }}
      >
        <strong>ניהול כבעלים: {session.businessName}</strong>
        <span>כל שינוי שנעשה כאן נשמר לעסק הזה ונרשם ביומן.</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {/* The countdown is a Latin-digit m:ss glued to Hebrew text. Without
              isolation the bidi algorithm reorders the two around the colon,
              the same failure `_lib/format.ts`'s ltrRanges fixes for time
              ranges. <bdi> is the markup form of that isolate. */}
          ההרשאה מסתיימת בעוד <bdi>{formatRemaining(remainingMs)}</bdi>
        </span>
        <button type="button" style={trailingControl} onClick={handleExit}>
          יציאה
        </button>
      </div>
    </>
  );
}

/**
 * The session ended by itself — the countdown ran out, or an impersonated
 * request answered 401 (see `auth-fetch.ts`, which never refreshes one).
 * Dropping the strip silently is the one thing this must not do: the admin
 * would keep typing into a page they believe belongs to someone else.
 */
function ExpiredNotice({ onDismiss }: { onDismiss: () => void }) {
  const barRef = useRef<HTMLDivElement | null>(null);
  useViewportOffset(barRef);

  return (
    <>
      <div aria-hidden="true" style={{ ...frame, borderColor: 'var(--error)' }} />
      <div
        ref={barRef}
        dir="rtl"
        lang="he"
        role="alert"
        data-testid="impersonation-expired"
        style={{ ...bar, background: 'var(--error-wash)', borderBottom: '1px solid var(--error)' }}
      >
        <strong>ניהול כבעלים הסתיים</strong>
        <span>חזרת לחשבון שלך. שום פעולה כאן אינה נשמרת לאותו עסק.</span>
        <a href="/admin/businesses" style={{ color: 'inherit', fontWeight: 600 }}>
          חזרה לקונסולה
        </a>
        <button type="button" style={trailingControl} onClick={onDismiss}>
          סגירה
        </button>
      </div>
    </>
  );
}
